import React, { useEffect, useRef } from 'react';
import { DARK_COLOR_FACTOR, LIGHT_COLOR_FACTOR } from './constants';
import { pSBC } from './functions';

export const useInterval = (callback, delay, deps = []) => {
	const savedCallback = useRef();

	// Remember the latest callback.
	useEffect(() => {
		savedCallback.current = callback;
	});

	// Set up the interval.
	// eslint-disable-next-line consistent-return
	useEffect(() => {
		function tick() {
			savedCallback.current();
		}
		if (delay !== null) {
			const id = setInterval(tick, delay);
			return () => clearInterval(id);
		}
	}, [delay, ...deps]);
};

export const usePolling = (cb, interval, deps = []) => {
	const [visible, setVisible] = React.useState(!document.hidden);
	const [online, setOnline] = React.useState(navigator.onLine);
	const inThrottle = React.useRef(false);

	function handleVisibilityChange() {
		setVisible(!document.hidden);
	}

	function handleConnectionChange(event) {
		if (event.type === 'online') {
			setOnline(true);
		}

		if (event.type === 'offline') {
			setOnline(false);
		}
	}

	React.useEffect(() => {
		document.addEventListener('visibilitychange', handleVisibilityChange, false);
		window.addEventListener('online', handleConnectionChange);
		window.addEventListener('offline', handleConnectionChange);
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('online', handleConnectionChange);
			window.removeEventListener('offline', handleConnectionChange);
		};
	}, []);


	React.useEffect(() => {
		if (visible && online && !inThrottle.current) {
			cb();
			inThrottle.current = true;
			setTimeout(() => {
				inThrottle.current = false;
			}, interval);
		}
	}, [visible, online]);

	useInterval(cb, !online ? interval * 1000 : visible ? interval : interval * 10, deps);
};

export const useDownloadDocument = () => {
	const [downloading, setDownloading] = React.useState(false);

	const download = async (path, filename) => {
		setDownloading(true);
		const token = sessionStorage.getItem('token');
		const apiToken = sessionStorage.getItem('apiToken');
		const participantToken = sessionStorage.getItem('participantToken');
		const response = await fetch(path, {
			headers: new Headers({
				'x-jwt-token': token || apiToken || participantToken,
				// 'x-jwt-token': token ? token : apiToken ? apiToken : participantToken,
			})
		});

		if (response.status === 200) {
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
		}
		setDownloading(false);
	};
	return [downloading, download];
};

export function useColors(colorObject) {
	const [colors, setColors] = React.useState(colorObject);

	const changePrimaryColor = primary => {
		setColors({
			...colors,
			text: primary,
			light: pSBC(LIGHT_COLOR_FACTOR, primary, false, true),
			dark: pSBC(-DARK_COLOR_FACTOR, primary, false, true),
		});
	};

	const recalculateWithPrimary = () => {
		setColors({
			...colors,
			text: colors.primary,
			light: pSBC(LIGHT_COLOR_FACTOR, colors.primary, false, true),
			dark: pSBC(-DARK_COLOR_FACTOR, colors.primary, false, true),
		});
	};

	React.useEffect(() => {
		changePrimaryColor(colorObject.primary);
	}, []);

	return {
		colors,
		setColors,
		changePrimaryColor,
		recalculateWithPrimary
	};
}
